import { useHeader } from '@/contexts';
import { useListaDocumentos } from '@/services/Documentos';
import type { Documento } from '@/services/Documentos';
import { getPeriodoUltimosNDias } from '@/lib/utils/formatters';
import {
  DocumentosContext,
  type DocumentosContextValue,
  type DocumentosListaData,
  type DocumentosModeloItem,
  type FiltrosDocumentos,
  type FiltrosHeaderSnapshot,
  type TipoDocumento,
} from '@/contexts/DocumentosContext';
import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { mesclarFiltros } from './filtros-da-lista';

interface FiltrosPersonalizados {
  fornecedor?: string;
  numeroDoc?: string;
  status?: string;
  evento?: string;
  chaveNota?: string;
  busca?: string;
  finalidade?: string;
}

const MODELOS_DOCUMENTOS = {
  nfe: { nome: 'NF-e (Modelo 55)', key: 'nfe' },
  nfag: { nome: 'NFAg (Modelo 75)', key: 'nfag' },
  nfc: { nome: 'NFC-e (Modelo 65)', key: 'nfc' },
  cte: { nome: 'CT-e (Modelo 57)', key: 'cte' },
  sat: { nome: 'SAT (Modelo 59)', key: 'sat' },
  nfcom: { nome: 'NFCom (Modelo 62)', key: 'nfcom' },
  nfgas: { nome: 'NFGas (Modelo 76)', key: 'nfgas' },
  nf3e: { nome: 'NF3-e (Modelo 66)', key: 'nf3e' },
} as const;

const MODELO_CODE_TO_KEY: Record<string, string> = {
  '55': 'nfe',
  '65': 'nfc',
  '57': 'cte',
  '59': 'sat',
  '62': 'nfcom',
  '66': 'nf3e',
  '75': 'nfag',
  '76': 'nfgas',
};

const FILTROS_INICIAIS: FiltrosDocumentos = {
  modelo: 'TODOS',
  cfop: '',
  cst: '',
  fornecedor: '',
  numeroDoc: '',
  status: '',
  evento: '',
  chaveNota: '',
  busca: '',
  finalidade: '',
};

function filtrarDocumentosPorModelo(
  documentosPorModelo: DocumentosModeloItem[],
  filtroModelo: string
): DocumentosModeloItem[] {
  if (filtroModelo === 'TODOS') {
    return documentosPorModelo;
  }
  const modeloKey = MODELO_CODE_TO_KEY[filtroModelo];
  return documentosPorModelo.filter(modelo => modelo.key === modeloKey);
}

function useDocumentosLista(params: {
  tipoDocumento: TipoDocumento;
  filtroModelo: string;
  filtrosPersonalizados?: FiltrosPersonalizados;
  filtrosHeaderAplicados: FiltrosHeaderSnapshot;
}) {
  // Processar fornecedor como CNPJ ou razão social
  const fornecedorValor = params.filtrosPersonalizados?.fornecedor || '';
  const isCNPJ = /^\d{14}$/.test(fornecedorValor.replace(/\D/g, ''));

  const query = useListaDocumentos(
    {
      ...params.filtrosHeaderAplicados,
      entradaSaida: params.tipoDocumento,
      paginaNumero: 1,
      paginaQuantidadePor: 500,
      cnpj: isCNPJ ? fornecedorValor.replace(/\D/g, '') : undefined,
      razaoSocial: !isCNPJ && fornecedorValor ? fornecedorValor : undefined,
      numeroDoc: params.filtrosPersonalizados?.numeroDoc || undefined,
      status: params.filtrosPersonalizados?.status ?? '',
      evento: params.filtrosPersonalizados?.evento ?? '',
      chaveAcesso: params.filtrosPersonalizados?.chaveNota || undefined,
      busca: params.filtrosPersonalizados?.busca || undefined,
      finalidade: params.filtrosPersonalizados?.finalidade || undefined,
    },
    {
      enabled: false,
    }
  );

  const documentosPorModeloCompleto: DocumentosModeloItem[] = Object.entries(
    MODELOS_DOCUMENTOS
  ).map(([key, config]) => {
    const documentosDoModelo =
      query.data?.data?.documentos?.[key as keyof typeof query.data.data.documentos];

    return {
      key,
      nome: config.nome,
      documentos: documentosDoModelo?.itens || [],
      total: documentosDoModelo?.totalItens || 0,
      situacoes: documentosDoModelo?.situacoes,
      valorTotal: documentosDoModelo?.valorTotal,
    };
  });

  const documentos = documentosPorModeloCompleto.reduce((acc, modelo) => {
    return [...acc, ...modelo.documentos];
  }, [] as Documento[]);

  const documentosPorModelo = filtrarDocumentosPorModelo(
    documentosPorModeloCompleto,
    params.filtroModelo
  );

  return {
    documentosPorModelo,
    documentosPorModeloCompleto,
    documentos,
    totalItens: query.data?.totalItens || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  } satisfies DocumentosListaData;
}

export function DocumentosProvider({ children }: PropsWithChildren) {
  const { filtros: filtrosHeader } = useHeader();

  const [tipoDocumento, setTipoDocumento] = useState<TipoDocumento>('0');
  const [filtros, setFiltros] = useState<FiltrosDocumentos>(FILTROS_INICIAIS);

  const [filtrosHeaderAplicados, setFiltrosHeaderAplicados] = useState<FiltrosHeaderSnapshot>(
    () => ({
      empresaId: filtrosHeader.empresaId,
      empresaCnpjs: filtrosHeader.empresaCnpjs,
      dataInicio: filtrosHeader.dataInicio,
      dataFim: filtrosHeader.dataFim,
    })
  );

  const aplicarTokenRef = useRef(0);
  const [aplicarToken, setAplicarToken] = useState(0);
  // Só "Buscar" e "Limpar filtros": o aviso do Recebimento depende da empresa e do período, não da busca, da
  // finalidade nem da janela Filtros, e não deve recarregar com eles.
  const [buscas, setBuscas] = useState(0);

  const dadosEntradas = useDocumentosLista({
    tipoDocumento: '0',
    filtroModelo: filtros.modelo,
    filtrosPersonalizados: {
      fornecedor: filtros.fornecedor,
      numeroDoc: filtros.numeroDoc,
      status: filtros.status,
      evento: filtros.evento,
      chaveNota: filtros.chaveNota,
      busca: filtros.busca,
      finalidade: filtros.finalidade,
    },
    filtrosHeaderAplicados,
  });

  const dadosSaidas = useDocumentosLista({
    tipoDocumento: '1',
    filtroModelo: filtros.modelo,
    filtrosPersonalizados: {
      fornecedor: filtros.fornecedor,
      numeroDoc: filtros.numeroDoc,
      status: filtros.status,
      evento: filtros.evento,
      chaveNota: filtros.chaveNota,
      busca: filtros.busca,
      finalidade: filtros.finalidade,
    },
    filtrosHeaderAplicados,
  });

  const refetchDocumentos = useCallback(() => {
    dadosEntradas.refetch();
    dadosSaidas.refetch();
  }, [dadosEntradas, dadosSaidas]);

  useEffect(() => {
    if (aplicarToken === 0) return;
    if (aplicarTokenRef.current === aplicarToken) return;
    aplicarTokenRef.current = aplicarToken;
    refetchDocumentos();
  }, [aplicarToken, refetchDocumentos]);

  useEffect(() => {
    // Busca inicial apenas ao entrar na tela de Documentos
    setAplicarToken(prev => prev + 1);
  }, []);

  const handleAplicarFiltros = useCallback((filtro: Partial<FiltrosDocumentos>) => {
    setFiltros(prev => mesclarFiltros(prev, filtro));
    setAplicarToken(prev => prev + 1);
  }, []);

  const handleBuscar = useCallback(() => {
    setFiltrosHeaderAplicados({
      empresaId: filtrosHeader.empresaId,
      empresaCnpjs: filtrosHeader.empresaCnpjs,
      dataInicio: filtrosHeader.dataInicio,
      dataFim: filtrosHeader.dataFim,
    });
    setAplicarToken(prev => prev + 1);
    setBuscas(prev => prev + 1);
  }, [
    filtrosHeader.dataFim,
    filtrosHeader.dataInicio,
    filtrosHeader.empresaId,
    filtrosHeader.empresaCnpjs,
  ]);

  /*
    Decisão 11 da SPEC-017: depois de um "Buscar" no cabeçalho, esta tela abre já buscada
    com o mesmo recorte. `filtros.aplicado` diz que houve aquele clique, e ele se desfaz
    quando a pessoa mexe no recorte — então isto não dispara enquanto ela escolhe.

    A guarda contra laço é o PRÓPRIO instantâneo: enquanto ele estiver defasado do
    cabeçalho, há o que aplicar; aplicar o iguala, e o efeito para. Este provider não tem
    `buscaAplicada` como os outros três, e inventar um estado só para isso seria guardar
    duas vezes a mesma informação.
  */
  const recorteDefasado =
    filtrosHeaderAplicados.empresaId !== filtrosHeader.empresaId ||
    filtrosHeaderAplicados.empresaCnpjs !== filtrosHeader.empresaCnpjs ||
    filtrosHeaderAplicados.dataInicio !== filtrosHeader.dataInicio ||
    filtrosHeaderAplicados.dataFim !== filtrosHeader.dataFim;

  useEffect(() => {
    if (!filtrosHeader.aplicado) return;
    if (!recorteDefasado) return;
    handleBuscar();
  }, [filtrosHeader.aplicado, recorteDefasado, handleBuscar]);

  const handleLimparFiltrosGlobais = useCallback(() => {
    // Mantém comportamento atual: reseta empresa e aplica um período padrão
    const periodoPadrao = getPeriodoUltimosNDias(30);

    setFiltrosHeaderAplicados({
      empresaId: null,
      empresaCnpjs: null,
      dataInicio: periodoPadrao.dataInicio,
      dataFim: periodoPadrao.dataFim,
    });
    // "Limpar todos os filtros" limpa também a busca e a finalidade do topo (SPEC-018).
    setFiltros(prev => ({ ...prev, busca: '', finalidade: '' }));
    setAplicarToken(prev => prev + 1);
    setBuscas(prev => prev + 1);
  }, []);

  const value = useMemo(
    (): DocumentosContextValue => ({
      tipoDocumento,
      setTipoDocumento,
      filtros,
      filtrosHeaderAplicados,
      handleBuscar,
      handleLimparFiltrosGlobais,
      handleAplicarFiltros,
      refetchDocumentos,
      dadosEntradas,
      dadosSaidas,
      aplicacao: buscas,
    }),
    [
      buscas,
      dadosEntradas,
      dadosSaidas,
      filtros,
      filtrosHeaderAplicados,
      handleAplicarFiltros,
      handleBuscar,
      handleLimparFiltrosGlobais,
      refetchDocumentos,
      tipoDocumento,
    ]
  );

  return <DocumentosContext.Provider value={value}>{children}</DocumentosContext.Provider>;
}
